import { atom } from "jotai";
import { atomFamily } from "jotai/utils";
import { Page, InterfacePageFetchResult } from "../types";
import {
  extractErrorMessageFromResponse,
  isTemplateInterfacePageId,
} from "../utils";

export const fetchInterfacePageAtomFamily_SUSPENDS = atomFamily(
  ({
    interfacePageId,
    interfaceProjectId,
  }: {
    interfacePageId: string | undefined;
    interfaceProjectId: string | undefined;
  }) => {
    const baseAtom = atom(
      async (): Promise<InterfacePageFetchResult | null | undefined> => {
        if (!interfacePageId || !interfaceProjectId) {
          return;
        }

        const isTemplateInterfacePage =
          isTemplateInterfacePageId(interfacePageId);

        if (isTemplateInterfacePage) {
          // Because we don't want to fetch with template asset id and get a 404 Not Found error
          return;
        }

        try {
          const res = await fetch(
            `/api/canvasui/interfaces/${interfaceProjectId}/pages/${interfacePageId}`,
          );

          /* ---------- success ---------- */
          if (res.ok) {
            const data = await res.json();
            const page: Page = {
              blocks: data.content.blocks,
              name: data.name,
              id: data.id,
              slug: data.slug,
            };
            return { type: "success", data: page };
          }

          /* ---------- expected errors surfaced to UI ---------- */
          if (res.status === 404) {
            return { type: "not_found" };
          }
          if (res.status === 403) {
            return { type: "forbidden" };
          }

          /* ---------- 5xx or other unexpected errors – log & degrade ---------- */
          const errorMessage = await extractErrorMessageFromResponse(res);
          console.error(
            `Failed to fetch interface page ${interfacePageId}: ${errorMessage}`,
          );
          return null;
        } catch (error) {
          console.error("Network error fetching interface page:", error);
          return null;
        }
      },
    );

    baseAtom.debugLabel = `fetchInterfacePageAtomFamily_SUSPENDS({interfacePageId: ${interfacePageId}, interfaceProjectId: ${interfaceProjectId}})`;
    return baseAtom;
  },
  (a, b) =>
    a.interfacePageId === b.interfacePageId &&
    a.interfaceProjectId === b.interfaceProjectId,
);
